import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Page,
  Card,
  TextField,
  Button,
  BlockStack,
  Divider,
  Text,
  Banner,
  InlineStack,
  Checkbox
} from '@shopify/polaris';
import axios from 'axios';
import { AppContext } from '../App';

function AutomationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { shop, setShop, showToast, API } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [billingStatus, setBillingStatus] = useState(null);

  useEffect(() => {
    const shopParam = searchParams.get('shop') || localStorage.getItem('shop');
    if (shopParam) {
      setShop(shopParam);
      fetchSettings(shopParam);
      fetchLogs(shopParam);
      fetchBillingStatus(shopParam);
    } else {
      navigate('/');
    }
  }, []);

  const fetchSettings = async (shopDomain) => {
    try {
      const response = await axios.get(`${API}/automation/settings`, {
        params: { shop: shopDomain }
      });
      setSettings(response.data);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to fetch settings', true);
    }
  };

  const fetchLogs = async (shopDomain) => {
    try {
      const response = await axios.get(`${API}/automation/logs`, {
        params: { shop: shopDomain }
      });
      setLogs(response.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch logs');
    }
  };

  const fetchBillingStatus = async (shopDomain) => {
    try {
      const response = await axios.get(`${API}/billing/status`, {
        params: { shop: shopDomain }
      });
      setBillingStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch billing status');
    }
  };

  const handleSaveSettings = async () => {
    if (billingStatus && billingStatus.plan !== 'pro' && billingStatus.plan !== 'trial') {
      showToast('Automation requires Pro plan', true);
      navigate(`/billing?shop=${shop}`);
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API}/automation/settings`,
        { ...settings, shop },
        { params: { shop } }
      );
      showToast('Automation settings saved!');
      fetchLogs(shop);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save settings', true);
    } finally {
      setLoading(false);
    }
  };

  const handleUnpause = async () => {
    setSettings({
      ...settings,
      is_paused: false,
      pause_reason: null
    });
  };

  if (!settings) {
    return <Page title="Automation"><Card><p>Loading...</p></Card></Page>;
  }

  return (
    <Page
      title="Automation Settings"
      backAction={{ onAction: () => navigate(`/orders?shop=${shop}`) }}
    >
      <BlockStack gap="400">
        {billingStatus && billingStatus.plan !== 'pro' && billingStatus.plan !== 'trial' && (
          <Banner tone="warning">
            <p>Automation features require Pro plan. <a href={`/billing?shop=${shop}`}>Upgrade now</a></p>
          </Banner>
        )}

        {settings.is_paused && (
          <Banner tone="critical" data-testid="automation-paused-banner">
            <BlockStack gap="200">
              <Text variant="bodyMd" as="p" fontWeight="semibold">Automation Paused</Text>
              <Text variant="bodyMd" as="p">{settings.pause_reason}</Text>
              <Button onClick={handleUnpause} data-testid="unpause-automation-btn">Resume Automation</Button>
            </BlockStack>
          </Banner>
        )}

        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Auto-Label on Payment</Text>
            <Divider />
            <Checkbox
              label="Automatically create shipping label when order is paid"
              checked={settings.auto_label_on_paid}
              onChange={(checked) => setSettings({ ...settings, auto_label_on_paid: checked })}
              disabled={settings.is_paused}
              data-testid="auto-label-checkbox"
            />
            <Text variant="bodyMd" as="p" tone="subdued">
              Automatically creates shipping labels using your configured rate strategy. Automation will pause automatically if errors occur.
            </Text>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Scheduled Bulk Processing</Text>
            <Divider />
            <Checkbox
              label="Enable daily bulk processing"
              checked={settings.scheduled_bulk_enabled}
              onChange={(checked) => setSettings({ ...settings, scheduled_bulk_enabled: checked })}
              disabled={settings.is_paused}
              data-testid="scheduled-bulk-checkbox"
            />
            <TextField
              label="Processing Time (24-hour format)"
              value={settings.scheduled_time}
              onChange={(value) => setSettings({ ...settings, scheduled_time: value })}
              placeholder="18:00"
              disabled={!settings.scheduled_bulk_enabled || settings.is_paused}
              helpText="Example: 18:00 for 6 PM"
              data-testid="scheduled-time-input"
            />
            <Text variant="bodyMd" as="p" tone="subdued">
              Process all unfulfilled orders daily at the specified time.
            </Text>
          </BlockStack>
        </Card>

        <Button
          primary
          onClick={handleSaveSettings}
          loading={loading}
          fullWidth
          data-testid="save-automation-btn"
        >
          Save Automation Settings
        </Button>

        {logs.length > 0 && (
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Recent Activity</Text>
              <Divider />
              <BlockStack gap="200">
                {logs.slice(0, 10).map((log, index) => (
                  <div key={index} style={{ padding: '8px', borderBottom: '1px solid #e1e3e5' }}>
                    <InlineStack align="space-between">
                      <Text variant="bodyMd" as="p">
                        {log.log_type === 'auto_label' ? '🏷️ Auto Label' : '📦 Bulk Processing'}
                        {log.order_number && ` - ${log.order_number}`}
                      </Text>
                      <Text variant="bodyMd" as="p" tone={log.status === 'success' ? 'success' : log.status === 'failed' ? 'critical' : 'subdued'}>
                        {log.status}
                      </Text>
                    </InlineStack>
                    <Text variant="bodySm" as="p" tone="subdued">{log.message}</Text>
                    <Text variant="bodySm" as="p" tone="subdued">{new Date(log.created_at).toLocaleString()}</Text>
                  </div>
                ))}
              </BlockStack>
            </BlockStack>
          </Card>
        )}
      </BlockStack>
    </Page>
  );
}

export default AutomationPage;